import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
interface NavLink {
  href: string;
  labelKey: 'nav.about' | 'nav.practice' | 'nav.results' | 'nav.cta';
}
const NAV_LINKS: NavLink[] = [{
  href: '#about',
  labelKey: 'nav.about'
}, {
  href: '#practice',
  labelKey: 'nav.practice'
}, {
  href: '#testimonials',
  labelKey: 'nav.results'
}, {
  href: '#cta',
  labelKey: 'nav.cta'
}];
export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close on escape
  useEffect(() => {
    if (!menuOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [menuOpen]);
  return <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-400 ease-in-out ${scrolled || menuOpen ? 'bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-glass-border' : 'bg-gradient-to-b from-black/90 to-transparent'}`} data-id="element-181">
      <div className={`px-8 lg:px-[60px] flex items-center justify-between transition-all duration-400 ${scrolled || menuOpen ? 'py-4' : 'py-6'}`} data-id="element-182">
        <div className="flex items-center gap-3 leading-none" data-id="element-183">
          <img src="https://cdn.magicpatterns.com/uploads/9mEgRo94BdLBoXFgduhDkY/logo.png" alt="Elias Navarro Law Logo" className="h-10 lg:h-12 w-auto object-contain" data-id="element-184" />
          <div className="flex flex-col" data-id="element-185">
            <span className="font-serif text-[14px] lg:text-[16px] font-bold tracking-[3px] text-white uppercase leading-tight" data-id="element-186">
              Elias Navarro
            </span>
            <span className="text-[8px] lg:text-[9px] tracking-[5px] text-gold-light uppercase font-light mt-1" data-id="element-187">
              Law Firm
            </span>
          </div>
        </div>

        {/* Desktop nav (preserved) */}
        <ul className="hidden md:flex items-center gap-10 list-none" data-id="element-188">
          <li data-id="element-189">
            <a href="#about" className="text-[#f5f5f5]/60 hover:text-white no-underline text-[11px] tracking-[2.5px] uppercase font-medium transition-colors duration-300 relative nav-link-underline" data-id="element-190">
              {t('nav.about')}
            </a>
          </li>
          <li data-id="element-191">
            <a href="#practice" className="text-[#f5f5f5]/60 hover:text-white no-underline text-[11px] tracking-[2.5px] uppercase font-medium transition-colors duration-300 relative nav-link-underline" data-id="element-192">
              {t('nav.practice')}
            </a>
          </li>
          <li data-id="element-193">
            <a href="#testimonials" className="text-[#f5f5f5]/60 hover:text-white no-underline text-[11px] tracking-[2.5px] uppercase font-medium transition-colors duration-300 relative nav-link-underline" data-id="element-194">
              {t('nav.results')}
            </a>
          </li>
          <li className="flex items-center gap-6" data-id="element-195">
            <LanguageSwitcher data-id="element-196" />
            <a href="#cta" className="bg-crimson text-white px-7 py-3 rounded-sm tracking-[2px] font-semibold transition-all duration-300 hover:bg-crimson-glow hover:-translate-y-[1px] hover:shadow-[0_8px_30px_rgba(192,57,43,0.4)] text-[11px] uppercase" data-id="element-197">
              {t('nav.cta')}
            </a>
          </li>
        </ul>

        {/* Mobile: language + hamburger */}
        <div className="md:hidden flex items-center gap-4" data-id="element-198">
          <LanguageSwitcher data-id="element-199" />
          <button type="button" aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} aria-controls="mobile-accordion-menu" onClick={() => setMenuOpen(o => !o)} className="relative w-10 h-10 flex items-center justify-center text-white border border-glass-border rounded-sm hover:bg-white/5 transition-colors duration-200" data-id="element-200">
            <AnimatePresence mode="wait" initial={false} data-id="element-201">
              {menuOpen ? <motion.span key="close" initial={{
              rotate: -90,
              opacity: 0
            }} animate={{
              rotate: 0,
              opacity: 1
            }} exit={{
              rotate: 90,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="inline-flex" data-id="element-202">
                  <X className="w-5 h-5" data-id="element-203" />
                </motion.span> : <motion.span key="open" initial={{
              rotate: 90,
              opacity: 0
            }} animate={{
              rotate: 0,
              opacity: 1
            }} exit={{
              rotate: -90,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="inline-flex" data-id="element-204">
                  <Menu className="w-5 h-5" data-id="element-205" />
                </motion.span>}
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Mobile accordion menu */}
      <AnimatePresence initial={false} data-id="element-206">
        {menuOpen && <motion.div id="mobile-accordion-menu" key="accordion" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: [0.22, 1, 0.36, 1]
      }} className="md:hidden overflow-hidden border-t border-glass-border bg-[#0a0a0a]/98 backdrop-blur-xl" data-id="element-207">
            <ul className="flex flex-col list-none px-8 py-4" data-id="element-208">
              {NAV_LINKS.map((link, i) => <motion.li key={link.href} initial={{
            opacity: 0,
            x: -16
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.35,
            delay: 0.08 + i * 0.06,
            ease: 'easeOut'
          }} className="border-b border-glass-border last:border-b-0" data-id="element-209">
                  <a href={link.href} onClick={() => setMenuOpen(false)} className="flex items-center justify-between py-5 text-[12px] tracking-[3px] uppercase text-[#f5f5f5]/70 hover:text-crimson-glow font-medium transition-colors duration-300 no-underline group" data-id="element-210">
                    <span className="flex items-center gap-4" data-id="element-211">
                      <span className="w-6 h-px bg-crimson group-hover:w-10 transition-all duration-300" data-id="element-212" />
                      {t(link.labelKey)}
                    </span>
                    <span className="text-crimson-glow text-[10px] tracking-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-213">
                      →
                    </span>
                  </a>
                </motion.li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}